<template>

    <div class="flex items-center justify-between mb-8">
        <Head :title="$t('suppliers.newSupplier')" />
        <page-title :title="$t('suppliers.newSupplier')"></page-title>
    </div>

    <jet-form-section @submitted="createSupplier">

        <template #form>

            <!-- Card General Project Information -->
            <form-card :title="$t('suppliers.address')" :description="$t('suppliers.addressDescription')">
                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.name") }} <RequiredMark /></label>
                    <input type="text" name="name" id="name" v-model="form.name"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="website" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.website") }}
                        <span class="text-gray-400">({{ $t("forms.optional") }})</span></label>
                    <input type="text" name="website" id="website" v-model="form.website"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.website" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="street" class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.street")
                        }} <RequiredMark /></label>
                    <input type="text" name="street" id="street" v-model="form.location.street"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['location.street']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-1">
                    <label for="zip" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.zip") }} <RequiredMark /></label>
                    <input type="text" name="zip" id="zip" v-model="form.location.zip"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.zip']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.city") }} <RequiredMark /></label>
                    <input type="text" name="city" id="city" v-model="form.location.city"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.city']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.country")
                        }} <RequiredMark /></label>
                    <Dropdown v-model="form.location.country"
                              :options="countries"
                              optionValue="code"
                              optionLabel="name"
                              :placeholder="$t('forms.selectCountry')"
                              class="mt-1 w-full" />
                    <jet-input-error :message="form.errors['location.country']" class="mt-2" />
                </div>

                <hr class="w-full" />

            </form-card>

            <!-- Card Project Contact -->
            <form-card :title="$t('suppliers.contact')" :description="$t('suppliers.contactDescription')">

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="firstname" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.firstname")
                        }} <RequiredMark /></label>
                    <input type="text" name="firstname" id="firstname" v-model="form.contact.firstname"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.firstname']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="lastname" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.lastname")
                        }} <RequiredMark /></label>
                    <input type="text" name="lastname" id="lastname" v-model="form.contact.lastname"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.lastname']" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="email" class="block text-sm font-medium text-gray-700">{{ $t("contact.email") }} <RequiredMark /></label>
                    <input type="text" name="email" id="email" v-model="form.contact.email"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.email']" class="mt-2" />
                </div>


                <div class="col-span-6">
                    <label for="phone" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.mobile")
                        }} <RequiredMark /></label>
                    <input type="text" name="mobile" id="mobile" v-model="form.contact.mobile"
                           :maxlength="$page.props.branding.mobile_number_max_digit || 12"
                           @input="form.contact.mobile = form.contact.mobile.replace(/[^0-9]/g, '').slice(0, $page.props.branding.mobile_number_max_digit || 12)"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors['contact.mobile']" class="mt-2" />
                </div>
            </form-card>
        </template>

        <template #actions>
            <div class="flex justify-end mt-4">
                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    {{ $t("actions.save") }}
                </primary-button>
            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import { countries } from "@/Shared/countries";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import { useForm } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import PageTitle from "@/Shared/Components/PageTitle";
import { Head } from "@inertiajs/inertia-vue3";
import RequiredMark from "@/Shared/Components/RequiredMark";

export default defineComponent({
    props: [],
    layout: MainLayout,
    components: {
        Head,
        PageTitle,
        FormCard,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        RequiredMark
    },
    data() {
        return {
            countries,
        };
    },
    methods: {
        createSupplier() {
            this.form.post(this.route("advisors.suppliers.store"), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'suppliers.created'
                })
            });
        }
    },
    setup() {
        const form = useForm({
            name: null,
            website: null,
            number: null,
            image: null,
            location: {
                street: null,
                zip: null,
                city: null,
                country: null
            },
            contact: {
                company: null,
                firstname: null,
                lastname: null,
                mobile: null,
                email: null
            }
        });

        return { form };
    }
});
</script>

<style scoped></style>
